import { Node } from 'react-flow-renderer';
import { Form, Input, Switch, message } from 'antd';
import { useEffect, useState, useImperativeHandle, useMemo } from 'react';
import OnTable from '@/components/on-table';
import {
  TableData,
  Kind,
  IsKind,
  SwitchListItem,
  DataType,
  ConditionItem,
} from './index';

import { useDispatch, useSelector } from 'umi';

import _ from 'lodash';
import moment from 'moment';
import ConditionForm from './condition-form';

export const initNodeTableData = (nodeId: string, label: string) => {
  return {
    id: nodeId,
    label,
    isOnEnter: true,
    isOnReceive: false,
    isOnIdle: false,
    onEnter: [],
    onIdle: [],
    onReceive: [],
    condition: [],
  };
}

interface NodeFormProps {
  node: Node;
  initData: TableData;
  nodeChange: (node: Node) => void;
  setTableData: (data: TableData) => void;
  onRef: React.Ref<unknown>;
}

export enum SWITCH_EVENT {
  初入 = '初入',
  挽留 = '挽留',
  匹配 = '匹配',
}

const switchList: SwitchListItem[] = [
  {
    s: 'isOnEnter',
    t: 'onEnter',
    tooltip: '初入，与客户互动（大招呼或根据客户上一回复进行更深交流）',
    label: SWITCH_EVENT.初入,
  },
  {
    s: 'isOnIdle',
    t: 'onIdle',
    tooltip: '挽留，客户长时间未回复消息，使用话术进行挽留',
    label: SWITCH_EVENT.挽留,
  },
  {
    s: 'isOnReceive',
    t: 'onReceive',
    tooltip: '匹配，设定匹配规则，根据客户回复的内容判断是否能够进行下一条话术',
    label: SWITCH_EVENT.匹配,
  },
];

export const HandleEventMapByLabel = _.keyBy(switchList, "label");
export const HandleEventMapByT = _.keyBy(switchList, "t");

const NodeForm: React.FC<NodeFormProps> = ({
  initData: tableData,
  nodeChange,
  setTableData,
  onRef,
  node,
}) => {
  useImperativeHandle(onRef, () => {
    return {
      tableData,
    };
  });

  const [form] = Form.useForm();
  const [condition, setCondition] = useState<string>('');
  const [nodeKey, setNodeKey] = useState<string | null>(null);

  /** dva 状态管理 */
  const dispatch = useDispatch();
  const nodeSelectList = useSelector((state: any) => state.open.nodeSelectList);
  const nodeHiddenMap = useSelector((state: any) => state.open.nodeHiddenMap);
  const miniProgramList = useSelector(
    (state: any) => state.open.miniProgramList,
  );

  const getSwitchValue = useMemo(
    () => (kind: IsKind) => tableData[node.id][kind],
    [tableData[node.id]],
  );
  const getTableValue = useMemo(
    () => (kind: Kind) => tableData[node.id][kind],
    [tableData[node.id]],
  );
  const isOnReceiveMatchChar = useMemo(() => {
    return tableData[node.id]?.isOnReceive && _.find(tableData[node.id]?.onReceive, function (o) {
      return o.code === '匹配字段';
    });
  }, [tableData[node.id]?.onReceive, tableData[node.id]?.isOnReceive]);

  /** 切换节点 初始化tableData */
  useEffect(() => {
    if (!node) return;
    form.setFieldsValue({
      id: node.id,
      label: node.data.label,
      type: node.type,
    });
    if (!tableData[node.id]) {
      setTableData({
        ...tableData,
        [node.id]: { ...initNodeTableData(node.id, node.data.label), ...node.data },
      });
    }
  }, [node.id]);

  /**
   * @description 即时保存tableData更新的内容
   */
  const saveTableData = (kind: Kind, value: DataType[]) => {
    const newItem = { ...tableData[node.id], [kind]: value };
    const newData = {
      ...tableData,
      [node.id]: newItem,
    };
    setTableData(newData);
  };

  const setSwitchValue = (kind: IsKind, value: boolean) => {
    const newItem = { ...tableData[node.id], [kind]: value };
    const newData = {
      ...tableData,
      [node.id]: newItem,
    };
    setTableData(newData);
  };

  /** @description 交换匹配条件位置 */
  const replaceNodes = (newArr: ConditionItem[]) => {
    const newItem = {
      ...tableData[node.id],
      condition: newArr,
    };
    const newData = {
      ...tableData,
      [node.id]: newItem,
    };
    setTableData(newData);
  };

  /**
   * @description 当新增condition时，更新流程图edges 和 nodes
   */
  const addNodes = (nodeId: string = node.id) => {
    if (!dispatch || !condition) return message.warning('匹配的字段不能为空！');
    const key = !nodeKey
      ? 'n-' + moment(new Date()).valueOf().toString()
      : nodeKey;
    const isValid =
      _.findIndex(
        tableData[nodeId].condition,
        (c) => c.condition === condition || c.key === key,
      ) === -1;
    if (!isValid) return message.warning('该匹配字段或节点已存在！');
    const child_node = _.find(nodeSelectList, n => n.key === key);
    const parent_node_hidden_status = nodeHiddenMap[nodeId].slice(-1)[0];
    if (child_node && child_node.hidden) {
      return message.warning("请先将选择的子节点状态切换为展开！");
    }
    if (parent_node_hidden_status) {
      return message.warning("请先将父节点状态切换为展开！");
    }
    dispatch({
      type: 'open/addNode',
      payload: {
        type: 'defaultNode',
        source: nodeId,
        condition,
        key,
      },
    });
    handleConditionChange(condition, key, nodeId);
  };

  const handleConditionChange = (condition: string, key: string, nodeId: string) => {
    const arr = tableData[nodeId].condition ?? [];
    const newItem = {
      ...tableData[nodeId],
      condition: [...arr, { key, condition }],
    };
    const newData = {
      ...tableData,
      [nodeId]: newItem,
    };
    setTableData(newData);
    setNodeKey(null);
    setCondition('');
  };

  /**
   * @description 表示改变
   */
  const handleFormValuesChange = (e: string) => {
    const _node = { ...tableData[node.id], label: e };
    const newData = {
      ...tableData,
      [node.id]: _node,
    };
    setTableData(newData);
  };

  /**
   * @description 处理删除condition
   */
  const handleConditionDelete = (key: string) => {
    if (!dispatch || !key) return;
    dispatch({
      type: 'open/setDeleteEdgeTarget',
      payload: [{ source: node.id, target: key }],
    });
  };

  return tableData[node.id] && node ? (
    <>
      <Form
        labelCol={{ span: 5.5 }}
        form={form}
        onValuesChange={(e) => handleFormValuesChange(e.label)}
      >
        <Form.Item name="label" label="标识" tooltip="简要描述此节点作用">
          <Input placeholder="请简要描述此节点" />
        </Form.Item>

        <Form.Item name="type" label="节点类型">
          { node.type }
        </Form.Item>

        {switchList.map((vo) => (
          <Form.Item key={vo.s} label={vo.label} tooltip={vo.tooltip}>
            <Switch
              checked={getSwitchValue(vo.s)}
              style={{ margin: '6px 0' }}
              onChange={(value) => setSwitchValue(vo.s, value)}
              disabled={node.type === 'globalNode' && ['onIdle', 'onReceive'].includes(vo.t)}
            />
            {getSwitchValue(vo.s) && (
              <OnTable
                id={node.id}
                kind={vo.t}
                data={getTableValue(vo.t)}
                saveTableData={saveTableData}
              />
            )}
          </Form.Item>
        ))}

        {isOnReceiveMatchChar && (
          <Form.Item
            label="匹配字段"
            tooltip="匹配客户回复的关键字/词，下一步执行指定节点的话术"
          >
            <ConditionForm
              conditions={tableData[node.id].condition}
              nodeSelectList={nodeSelectList}
              currentNodeId={node.id}
              currentNodeType={node.type}
              nodeKey={nodeKey}
              setNodeKey={(e: string) => setNodeKey(e)}
              handleConditionDelete={handleConditionDelete}
              condition={condition}
              setCondition={(e: string) => setCondition(e)}
              addNodes={() => addNodes(node.id)}
              replaceNodes={replaceNodes}
            />
          </Form.Item>
        )}
      </Form>
    </>
  ) : (
    <div></div>
  );
};

export default NodeForm;
